/**
 * Face ID lock for the iOS app (1.1+): optional, per phone, off until the
 * customer turns it on (Profile, or the one-time offer on Trips). When on,
 * Defera asks for Face ID, or the phone's passcode as the fallback, when it
 * opens and when it comes back after two minutes away. Only a signed-in app
 * locks: with nobody signed in there is nothing private to protect.
 *
 * The setting lasts until sign-out, which clears the app's storage (Layout's
 * SIGNED_OUT handler), so whoever signs in next on this phone starts with it
 * off rather than facing someone else's Face ID.
 *
 * This guards the screen; the sign-in itself still lives in the app's storage.
 * The lock screen is FaceIdLock (components/native). On the web every
 * function here is a no-op.
 */
import { isNativeApp } from '@/native/bootstrap';

const SETTING_KEY = 'defera.faceIdLock.v1';
const OFFERED_KEY = 'defera.faceIdLock.offered.v1';
export const RELOCK_AFTER_MS = 2 * 60 * 1000;
const loadBiometric = () => import('@capgo/capacitor-native-biometric');

const browserStorage = () => { try { return localStorage; } catch { return null; } };
const stored = {
  get: (key) => { try { return localStorage.getItem(key); } catch { return null; } },
  set: (key, value) => { try { localStorage.setItem(key, value); } catch { /* private browsing */ } },
};

// While iOS shows a Face ID or permission prompt the app is briefly inactive;
// the privacy cover (FaceIdLock) stays down for those.
let promptsOpen = 0;
export const systemPromptOpen = () => promptsOpen > 0;
export async function duringSystemPrompt(fn) {
  promptsOpen += 1;
  try {
    return await fn();
  } finally {
    setTimeout(() => { promptsOpen -= 1; }, 300);
  }
}

export const faceIdLockEnabled = () => stored.get(SETTING_KEY) === 'on';
export const setFaceIdLock = (on) => stored.set(SETTING_KEY, on ? 'on' : 'off');
export const faceIdOffered = () => stored.get(OFFERED_KEY) === '1';
export const markFaceIdOffered = () => stored.set(OFFERED_KEY, '1');

/** Is someone signed in on this phone? Supabase's saved sign-in, read from
 *  storage so it's instant and works offline (like hasSavedSignIn in offline.js). */
export function signedInHere(storage = browserStorage()) {
  let count = 0;
  try { count = storage?.length || 0; } catch { return false; }
  for (let i = 0; i < count; i += 1) {
    try {
      const key = storage.key(i);
      if (/^sb-.+-auth-token$/.test(key || '') && JSON.parse(storage.getItem(key))?.refresh_token) return true;
    } catch {
      /* not a session */
    }
  }
  return false;
}

/** { available, kind: 'Face ID' | 'Touch ID' | 'passcode' }; unavailable when the phone has no passcode. */
export async function biometricAvailability({ native = isNativeApp, load = loadBiometric } = {}) {
  if (!native) return { available: false, kind: null };
  try {
    const { NativeBiometric, BiometryType } = await load();
    const result = await NativeBiometric.isAvailable({ useFallback: true });
    if (!result?.isAvailable) return { available: false, kind: null };
    // biometryType names the hardware even with no face or finger enrolled;
    // then only the passcode works.
    let kind = 'passcode';
    if (result.strongBiometryIsAvailable && result.biometryType === BiometryType.FACE_ID) kind = 'Face ID';
    if (result.strongBiometryIsAvailable && result.biometryType === BiometryType.TOUCH_ID) kind = 'Touch ID';
    return { available: true, kind };
  } catch {
    return { available: false, kind: null };
  }
}

/** Face ID, falling back to the passcode. true when the owner confirmed. */
export async function verifyOwner({ load = loadBiometric } = {}) {
  try {
    const { NativeBiometric } = await load();
    await duringSystemPrompt(() => NativeBiometric.verifyIdentity({
      reason: 'Unlock Defera',
      title: 'Unlock Defera',
      useFallback: true,
      fallbackTitle: 'Use Passcode',
      maxAttempts: 3,
    }));
    return true;
  } catch {
    return false;
  }
}

/** Whether to lock now. backgroundedAt null means the app just opened. */
export function shouldLock({ enabled, signedIn, backgroundedAt, now }) {
  if (!enabled || !signedIn) return false;
  if (backgroundedAt == null) return true;
  return now - backgroundedAt >= RELOCK_AFTER_MS;
}
